// Singleton Socket.IO client for cross-device live updates.
//
// Connects on first call, authenticates via the existing httpOnly JWT
// cookie, joins per-record rooms on subscribe(), and fans incoming
// `record:patched` events out to listeners. The serverside contract is in
// backend/nwd-backend/utils/realtime.js + server.js.
//
// Reconnect behaviour: on every reconnect the server may have missed
// events for us, so DayScreen refetches the current record rather than
// trying to replay events on the wire. Reconnect listeners get notified
// so they can do that refetch.

import { io } from "socket.io-client";

let socket = null;
let serverAssignedSocketId = null;

const patchListeners = new Set();
const reconnectListeners = new Set();
const subscriptions = new Set(); // "type::id" — re-emitted after reconnect

function key(type, id) {
  return `${type}::${id}`;
}

function ensureSocket() {
  if (socket) return socket;
  const url = process.env.REACT_APP_API_URL || "";

  socket = io(url, {
    // Pin to native WebSocket: long-poll fallback on Heroku just means a
    // flood of cookied HTTP requests. Modern browsers do WS over TLS
    // through the proxies we care about.
    transports: ["websocket"],
    // Send the JWT cookie on the upgrade request.
    withCredentials: true,
    reconnection: true,
    // Jitter spreads the thundering herd that hits us when a Heroku dyno
    // restarts (every ~24h, all clients reconnect simultaneously
    // otherwise).
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
    randomizationFactor: 0.5,
  });

  socket.on("connected", (msg) => {
    serverAssignedSocketId = msg?.socketId || socket.id;
    // Re-emit any active subscriptions after a reconnect — server-side
    // room membership is socket-scoped and is wiped on disconnect.
    for (const k of subscriptions) {
      const [recordType, recordId] = k.split("::");
      socket.emit("subscribe", { recordType, recordId });
    }
  });

  socket.io.on("reconnect", () => {
    serverAssignedSocketId = socket.id;
    for (const cb of reconnectListeners) {
      try {
        cb();
      } catch (e) {
        // Listeners are best-effort; one bad listener must not poison the rest.
      }
    }
  });

  socket.on("record:patched", (msg) => {
    for (const cb of patchListeners) {
      try {
        cb(msg);
      } catch (e) {
        // ditto
      }
    }
  });

  socket.on("connect_error", (err) => {
    // Auth failures or transport errors arrive here. Useful to surface in
    // dev; in production this just logs and reconnect timers keep ticking.
    // eslint-disable-next-line no-console
    console.warn("[socket] connect_error", err?.message);
  });

  return socket;
}

export function getSocketId() {
  return serverAssignedSocketId;
}

export function subscribe(recordType, recordId) {
  if (!recordType || !recordId) return;
  const s = ensureSocket();
  subscriptions.add(key(recordType, recordId));
  // If the socket isn't connected yet, the `connected` handler above will
  // re-emit on connect — so this is safe to call before connect completes.
  if (s.connected) {
    s.emit("subscribe", { recordType, recordId });
  }
}

export function unsubscribe(recordType, recordId) {
  if (!recordType || !recordId) return;
  subscriptions.delete(key(recordType, recordId));
  if (socket && socket.connected) {
    socket.emit("unsubscribe", { recordType, recordId });
  }
}

export function onPatch(handler) {
  patchListeners.add(handler);
  return () => patchListeners.delete(handler);
}

export function onReconnect(handler) {
  reconnectListeners.add(handler);
  return () => reconnectListeners.delete(handler);
}
